// Code 128 for sample labels (docs/plans/phase1-lab.md §5.9). In-house rather than a
// dependency: the symbology is a fixed table, a checksum and a stop pattern, and owning
// it lets the label snap every bar to whole printer dots.
//
// Code sets B (printable ASCII) and C (digit pairs) only; code set A (control characters)
// is never needed for a sample number. The output is the run of bar and space widths in
// modules, starting with a bar; the caller adds the quiet zones and draws it.

import { err, ok, type Result } from '@wodi/domain';

/**
 * Bar and space widths of symbol values 0–106, from the Code 128 specification
 * (ISO/IEC 15417). Each symbol is 3 bars and 3 spaces, 11 modules wide; the stop pattern
 * (106) has a final bar and is 13 modules. Checked in the tests: widths, parity, uniqueness.
 */
// prettier-ignore
export const CODE128_PATTERNS: readonly string[] = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212',
  '221213', '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221',
  '223211', '221132', '221231', '213212', '223112', '312131', '311222', '321122', '321221',
  '312212', '322112', '322211', '212123', '212321', '232121', '111323', '131123', '131321',
  '112313', '132113', '132311', '211313', '231113', '231311', '112133', '112331', '132131',
  '113123', '113321', '133121', '313121', '211331', '231131', '213113', '213311', '213131',
  '311123', '311321', '331121', '312113', '312311', '332111', '314111', '221411', '431111',
  '111224', '111422', '121124', '121421', '141122', '141221', '112214', '112412', '122114',
  '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111', '111242',
  '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311',
  '113141', '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
];

export const CODE128_START_B = 104;
export const CODE128_START_C = 105;
export const CODE128_SWITCH_TO_B = 100;
export const CODE128_SWITCH_TO_C = 99;
export const CODE128_STOP = 106;

/** The quiet zone each side of the symbol, in modules (the specification's minimum). */
export const CODE128_QUIET_ZONE_MODULES = 10;

export type Code128ErrorCode = 'barcode.empty' | 'barcode.unsupported_character';

export interface Code128Symbol {
  /** Symbol values from start to stop, checksum included. */
  readonly values: readonly number[];
  /** Bar and space widths in modules, alternating and starting with a bar. */
  readonly widths: readonly number[];
  /** The symbol's width in modules, without quiet zones. */
  readonly modules: number;
}

function isDigit(char: string | undefined): boolean {
  return char !== undefined && char >= '0' && char <= '9';
}

function digitRun(text: string, from: number): number {
  let end = from;
  while (isDigit(text[end])) end += 1;
  return end - from;
}

function codeSetBValue(char: string): number {
  return (char.codePointAt(0) ?? 0) - 32;
}

/**
 * Chooses code sets to keep the symbol short: C for runs of digits long enough to gain
 * (four at the start or end, six in the middle), B for everything else. Deterministic, so
 * a reprinted label has exactly the same bars.
 */
function symbolValues(text: string): number[] {
  const values: number[] = [];
  const lead = digitRun(text, 0);
  let set: 'B' | 'C';
  if (lead >= 4 || (lead === text.length && lead === 2)) {
    set = 'C';
    values.push(CODE128_START_C);
  } else {
    set = 'B';
    values.push(CODE128_START_B);
  }

  let i = 0;
  while (i < text.length) {
    const run = digitRun(text, i);
    if (set === 'C') {
      if (run >= 2) {
        values.push(Number(text.slice(i, i + 2)));
        i += 2;
      } else {
        values.push(CODE128_SWITCH_TO_B);
        set = 'B';
      }
      continue;
    }
    const reachesEnd = i + run === text.length;
    if (run >= 6 || (reachesEnd && run >= 4)) {
      // Code set C takes pairs; an odd digit out goes first, in code set B.
      if (run % 2 === 1) {
        values.push(codeSetBValue(text[i] ?? '0'));
        i += 1;
      }
      values.push(CODE128_SWITCH_TO_C);
      set = 'C';
      continue;
    }
    values.push(codeSetBValue(text[i] ?? ' '));
    i += 1;
  }
  return values;
}

/** The modulo 103 checksum: the start value plus each value times its position. */
export function code128Checksum(values: readonly number[]): number {
  let sum = values[0] ?? 0;
  for (let position = 1; position < values.length; position += 1) {
    sum += (values[position] ?? 0) * position;
  }
  return sum % 103;
}

/** Encodes printable ASCII (space to tilde) as a Code 128 symbol. */
export function encodeCode128(text: string): Result<Code128Symbol, Code128ErrorCode> {
  if (text === '') return err('barcode.empty');
  if (!/^[\x20-\x7e]+$/.test(text)) return err('barcode.unsupported_character');

  const data = symbolValues(text);
  const values = [...data, code128Checksum(data), CODE128_STOP];
  const widths: number[] = [];
  for (const value of values) {
    const pattern = CODE128_PATTERNS[value];
    if (pattern === undefined) throw new Error(`No Code 128 pattern for value ${value}`);
    for (const digit of pattern) widths.push(Number(digit));
  }
  const modules = widths.reduce((total, width) => total + width, 0);
  return ok({ values, widths, modules });
}
